import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  Component,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  Renderer2,
  ChangeDetectionStrategy,
} from "@angular/core";

import { DatePipe, DecimalPipe } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import * as Constants from "../bfx-blotter-filter/models/bfx-blotter-filter.constants";

import {
  ColumnFilterType,
  UserPromiseLookupService,
  AccountPromiseLookupService,
  ClientPromiseLookupService,
  TraderGroupPromiseLookupService,
  SalesGroupPromiseLookupService,
  CcyPromiseLookupService,
  CcyPairPromiseLookupService,
  CurrencyPairGroupPromiseLookupService,
  BranchPromiseLookupService,
  SysFeatures,
  FixingSourcePromiseLookupService,
  BenchmarkFixingPrimaryLookupService,
  CustomFieldLookupService,
} from "shared/cubiq-services";

import { ActiveUserProfilePromiseService } from "shared/auth-services";
import {
  ActiveUser,
  TradeTableDefinitionFactory,
  OrdersTableDefinitionFactory,
} from "shared/cubiq-services";

import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import { BfxBlotterFilterSelectorService } from "../bfx-blotter-filter/bfx-blotter-filter-selector.service";
import { BfxFindDealsFilterFieldName, SystemConfigPromiseService } from "shared/cubiq-services";
import { BfxFindFilterView } from "../bfx-find/bfx-find-filter-view";
import { BfxPanelHelperService } from "../bfx-common/services/bfx-panel-helper.service";
import { BfxFindDealsService } from "../bfx-find-deals/bfx-find-deals.service";

import { BfxFindDealsFilterDataFactory } from "./bfx-find-deals-filter-data-factory";

import {
  FIND_OBJECT_SORT,
  BfxFindOrderDealState,
  FilterData,
  BFX_FIND_TIMEZONE_CODES,
  BFX_FIND_TRADE_TYPES,
} from "../bfx-find/bfx-find.constants";

import { DomHandler } from "@bfx/primeng/dom";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";

@Component({
  selector: "bfx-find-deals-filter-view",
  templateUrl: "../bfx-find/html/bfx-find-filter-view.html",
  providers: [DatePipe, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxFindDealsFilterViewComponent extends BfxFindFilterView implements OnDestroy {
  protected primaryTypeName = CubiqTS.DealImpl.enType;

  constructor(
    blotterService: BfxBlotterFilterService,
    translationService: TranslateService,
    userService: UserPromiseLookupService,
    accountService: AccountPromiseLookupService,
    clientService: ClientPromiseLookupService,
    traderGroupService: TraderGroupPromiseLookupService,
    salesGroupService: SalesGroupPromiseLookupService,
    branchLookupService: BranchPromiseLookupService,
    ccyService: CcyPromiseLookupService,
    ccyPairService: CcyPairPromiseLookupService,
    currencyPairGroupService: CurrencyPairGroupPromiseLookupService,
    datePipe: DatePipe,
    activeUserService: ActiveUserProfilePromiseService,
    selectorService: BfxBlotterFilterSelectorService,
    panelHelper: BfxPanelHelperService,
    domHandler: DomHandler,
    cd: ChangeDetectorRef,
    decimalFormatter: DecimalPipe,
    systemConfigLookup: SystemConfigPromiseService,
    protected findService: BfxFindDealsService,
    protected fixingSourceService: FixingSourcePromiseLookupService,
    protected bmFixingSourceService: BenchmarkFixingPrimaryLookupService,
    renderer: Renderer2,
    el: ElementRef,
    //
    protected ordersDefinition: OrdersTableDefinitionFactory,
    protected tradeDefinition: TradeTableDefinitionFactory,
    filterDataFactory: BfxFindDealsFilterDataFactory,
    customFieldService: CustomFieldLookupService,
  ) {
    super(
      blotterService,
      translationService,
      userService,
      accountService,
      clientService,
      traderGroupService,
      salesGroupService,
      branchLookupService,
      ccyService,
      ccyPairService,
      currencyPairGroupService,
      datePipe,
      activeUserService,
      selectorService,
      panelHelper,
      domHandler,
      cd,
      decimalFormatter,
      systemConfigLookup,
      findService,
      fixingSourceService,
      bmFixingSourceService,
      customFieldService,
      renderer,
      el,
    );

    this.async_data = new Promise<FilterData[]>((res) => {
      let rolePromises: Array<Promise<any>> = [];
      rolePromises.push(
        this.systemConfigLookup.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      );
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.fwdEnabled));
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.ndfEnabled));
      rolePromises.push(this.systemConfigLookup.getSystemConfig(SysFeatures.benchmarksEnabled));

      Promise.all(rolePromises).then((values: Array<any>) => {
        this.clientTraderUsersEnabled = values[0].toUpperCase() == "Y";
        this.fwdEnabled = values[1].toUpperCase() == "Y";
        this.ndfEnabled = values[2].toUpperCase() == "Y";
        this.benchmarksEnabled = values[3].toUpperCase() == "Y";

        ordersDefinition.defineTable().then((def) => {
          this.tableDefinition = def;
          this.initTableDefinition(this.tableDefinition);
          this.initCustomFields();
          tradeDefinition.defineTable().then((tradeDef) => {
            tradeDef.columns.forEach((columnDef: BfxColDef) => {
              if (columnDef && columnDef.field === BfxFindDealsFilterFieldName.DEAL_STATE) {
                let keys: string[] = Object.keys(BfxFindOrderDealState);
                let name: string = columnDef.field;
                let values: any = keys.map((a) => {
                  return {
                    label: this.translationService.instant(
                      "column.filter." + columnDef.field + "." + a,
                    ),
                    value: a,
                  };
                });

                if (values) {
                  let primedData = values.sort(FIND_OBJECT_SORT);

                  if (BfxFindDealsFilterFieldName.DEAL_STATE === name) {
                    this.objectValues[BfxFindDealsFilterFieldName.DEAL_STATE] = primedData;
                    this.objectColumns[BfxFindDealsFilterFieldName.DEAL_STATE] =
                      Constants.OBJECT_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0;
                    this.columnFilterTypes[BfxFindDealsFilterFieldName.DEAL_STATE] =
                      ColumnFilterType[columnDef.columnFilterType];
                  }
                }
              }
            });

            this.initNonTableFindFields();

            let { data } = filterDataFactory.defineFilterData(
              this.clientTraderUsersEnabled,
              this.fwdEnabled,
              this.ndfEnabled,
              this.benchmarksEnabled,
            );

            this.data = data;
            this.isDataPrimed = true;

            res(this.data);
          });
        });
      });
    });

    this.filterRequestObservableSubscr = this.findService
      .getFilterRequestObservable()
      .subscribe(() => {
        // TODO clone
        this.findService.responseFilterView(this.data);
      });
  }

  public initNonTableFindFields() {
    this.columnFilterTypes[BfxFindDealsFilterFieldName.ORDER_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.TRADE_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.STRATEGY_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.EXTERNAL_REF] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.BOOKING_UTI] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.EXTERNAL_DEAL_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.NETTING_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.FWD_NETTING_ID] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.CLIENT_TRADER] = "CLIENT_TRADER";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.CCY] = "CURRENCY";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.CCY_PAIR_GROUP] = "CURRENCY_PAIR_GROUP";

    this.columnFilterTypes[BfxFindDealsFilterFieldName.SEARCH_IN_TZ] = "TIMEZONE_CD";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.ENTRY_DATE] = "DATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.FILL_DATE] = "DATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.VALUE_DATE] = "DATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.TRADE_DATE] = "DATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.FIXING_TIME] = "TIME";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.FIXING_DATE] = "DATE";

    this.columnFilterTypes[BfxFindDealsFilterFieldName.FILL_AMT] = "NUMBER";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.FILL_AMT_USD] = "NUMBER";

    this.columnFilterTypes[BfxFindDealsFilterFieldName.MARKET_SPOT] = "RATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.MARKET_ALL_IN] = "RATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.CLIENT_SPOT] = "RATE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.CLIENT_ALL_IN] = "RATE";

    this.columnFilterTypes[BfxFindDealsFilterFieldName.BM_FIXCODE] = "BM_FIXCODE";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.LAST_MARKET] = "STRING";

    this.columnFilterTypes[BfxFindDealsFilterFieldName.OBO_CLIENT] = "STRING";
    this.columnFilterTypes[BfxFindDealsFilterFieldName.OBO_ACCOUNT] = "STRING";

    this.tradeDefinition.defineTable().then((tradeDef) => {
      tradeDef.columns.forEach((columnDef: BfxColDef) => {
        if (columnDef && columnDef.field === BfxFindDealsFilterFieldName.DEAL_STATE) {
          let keys: string[] = Object.keys(BfxFindOrderDealState);
          let name: string = columnDef.field;
          let values: any = keys.map((a) => {
            return {
              label: this.translationService.instant("column.filter." + columnDef.field + "." + a),
              value: a,
            };
          });

          if (values) {
            let primedData = values.sort(FIND_OBJECT_SORT);

            if (BfxFindDealsFilterFieldName.DEAL_STATE === name) {
              this.objectValues[BfxFindDealsFilterFieldName.DEAL_STATE] = primedData;
              this.objectColumns[BfxFindDealsFilterFieldName.DEAL_STATE] =
                Constants.OBJECT_COLUMN_FILTER_TYPES.indexOf(columnDef.columnFilterType) >= 0;
              this.columnFilterTypes[BfxFindDealsFilterFieldName.DEAL_STATE] =
                ColumnFilterType[columnDef.columnFilterType];
            }
          }
        }
      });
    });

    let timezoneCdFieldData = BFX_FIND_TIMEZONE_CODES;
    this.columnFilterTypes[BfxFindDealsFilterFieldName.SEARCH_IN_TZ] = "TIMEZONE_CD";
    this.objectValues[BfxFindDealsFilterFieldName.SEARCH_IN_TZ] = timezoneCdFieldData;
    this.objectColumns[BfxFindDealsFilterFieldName.SEARCH_IN_TZ] = true;

    const tradeTypeFieldData = BFX_FIND_TRADE_TYPES;
    this.columnFilterTypes[BfxFindDealsFilterFieldName.TRADE_TYPE] = "TRADE_TYPE";
    this.objectValues[BfxFindDealsFilterFieldName.TRADE_TYPE] = tradeTypeFieldData;
    this.objectColumns[BfxFindDealsFilterFieldName.TRADE_TYPE] = true;

    if (this.benchmarksEnabled) {
      this.bmFixingSourceService.isPrimed().then(() => {
        this.bmFixingSourceService.getAll().then((values: Array<CubiqTS.BenchmarkFixingImpl>) => {
          if (values) {
            let primedData = values
              .map((a: CubiqTS.BenchmarkFixingImpl) => {
                return { label: a.benchmarkCd, value: a.benchmarkCd };
              })
              .sort(FIND_OBJECT_SORT);
            this.objectValues[BfxFindDealsFilterFieldName.BM_FIXCODE] = primedData;
            this.objectColumns[BfxFindDealsFilterFieldName.BM_FIXCODE] = true;
          }
        });
      });
    }

    this.ccyService.isPrimed().then(() => {
      this.ccyService.getAll().then((values: Array<CubiqTS.CurrencyImpl>) => {
        if (values) {
          let primedData = values
            .map((a) => {
              return { label: a.id, value: a.id };
            })
            .sort(FIND_OBJECT_SORT);
          this.objectValues[BfxFindDealsFilterFieldName.CCY] = primedData;
          this.objectColumns[BfxFindDealsFilterFieldName.CCY] = true;
        }
      });
    });

    this.ccyPairService.isPrimed().then(() => {
      this.currencyPairGroupService
        .getAll()
        .then((values: Array<CubiqTS.CurrencyPairGroupImpl>) => {
          if (values) {
            let primedData = values
              .map((a: CubiqTS.CurrencyPairGroup) => {
                return {
                  label: a.currencypairGroupNm,
                  value: a.groupMembership.map((m: CubiqTS.CurrencyPairGroupMember) => {
                    return {
                      label: `${m.baseCurrencyCd}/${m.nonBaseCurrencyCd}`,
                      value: `${m.baseCurrencyCd}${m.nonBaseCurrencyCd}`,
                    };
                  }),
                };
              })
              .sort(FIND_OBJECT_SORT);

            this.objectValues[BfxFindDealsFilterFieldName.CCY_PAIR_GROUP] = primedData;
            this.objectColumns[BfxFindDealsFilterFieldName.CCY_PAIR_GROUP] = true;
          }
        });
    });

    this.systemConfigLookup.getSystemConfig(SysFeatures.displayUserNames).then((cfg) => {
      this.showUserName = cfg == "true" || cfg == "TRUE" || cfg == "1" || cfg == "Y" || cfg == "y";

      this.userService.isPrimed().then(() => {
        this.userService.getAll().then((values: Array<CubiqTS.UserImpl>) => {
          this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
            this.primeClientTraderUserData(
              this.ALL_CLIENT_TRADERS,
              BfxFindDealsFilterFieldName.CLIENT_TRADER,
              values,
              activeUser,
            );
            this.primeUserData(BfxFindDealsFilterFieldName.ENTRY_USER, values, activeUser);
            this.primeUserData(BfxFindDealsFilterFieldName.FILL_USER, values, activeUser);
          });
        });
      });
    });
  }

  public primeTraderGroupData(colDef: BfxColDef, values: CubiqTS.TradingGroupImpl[]): void {
    let primedData = values
      .map((a) => {
        return { label: a.groupCode, value: a.groupId };
      })
      .sort(FIND_OBJECT_SORT);
    this.objectValues[BfxFindDealsFilterFieldName.TRADER_GROUP] = primedData;
    this.objectColumns[BfxFindDealsFilterFieldName.TRADER_GROUP] =
      Constants.OBJECT_COLUMN_FILTER_TYPES.indexOf(colDef.columnFilterType) >= 0;
    this.columnFilterTypes[BfxFindDealsFilterFieldName.TRADER_GROUP] =
      ColumnFilterType[colDef.columnFilterType];
  }

  public primeOrderTypeData(colDef: BfxColDef, values: any[]): void {
    let primedData = values.sort(FIND_OBJECT_SORT);

    this.objectValues[BfxFindDealsFilterFieldName.TYPE] = primedData;
    this.objectColumns[BfxFindDealsFilterFieldName.TYPE] =
      Constants.OBJECT_COLUMN_FILTER_TYPES.indexOf(colDef.columnFilterType) >= 0;
    this.columnFilterTypes[BfxFindDealsFilterFieldName.TYPE] =
      ColumnFilterType[colDef.columnFilterType];
  }

  public selectOption(data: any, event: any) {
    super.selectOption(data, event);

    if (data.fieldName == BfxFindDealsFilterFieldName.CLIENT_ID) {
      this.cacheSelectedClientsIds(data, BfxFindDealsFilterFieldName.CLIENT_TRADER);
    } else if (data.fieldName == BfxFindDealsFilterFieldName.CLIENT_NAME) {
      this.cacheSelectedClientsNames(data, BfxFindDealsFilterFieldName.CLIENT_TRADER);
    }
  }

  public resetVisibleClientTraderData(): void {
    this.cacheSelectedClientsIds({ valid: null }, BfxFindDealsFilterFieldName.CLIENT_TRADER);
    this.cacheSelectedClientsNames({ valid: null }, BfxFindDealsFilterFieldName.CLIENT_TRADER);
  }

  public processProductPrimeData(columnDef: BfxColDef): void {
    if (columnDef.headerName == "Product") {
      let primedValues: any[] = this.objectValues[columnDef.field];

      if (primedValues != null && Array.isArray(primedValues)) {
        primedValues.push({
          label: "Spot Reserve",
          value: "SpotReserve",
        });
      }
    }
  }

  /**
   * For columns with objectValues, return true if only a single value can be selected
   * @param columnName
   * @returns
   */
  public isSingleSelectColumn(columnName: string): boolean {
    switch (columnName) {
      case BfxFindDealsFilterFieldName.TRADE_TYPE:
        return true;
      default:
        return false;
    }
  }
}
